import { getCompanyDomain } from '@aibees/elephant/components/company-details/utils';
import { getCompanyInfoSectionVisibility } from '@aibees/elephant/components/company-info/utils';
import { EntityInfoPanel } from '@aibees/elephant/components/entity-info-panel';
import {
  SeaAllFilterField,
  useTagActions,
} from '@aibees/elephant/components/see-all-popup';
import { useCompanyReadDataContext } from '@aibees/elephant/context';
import { companyTypeFilterLabelParser } from '@aibees/elephant/feature/search-filters/components/filter-tags/utils';
import { getCompanySizeRange } from '@aibees/elephant/feature/search-table-company/utils';
import { formatCompanyLocation } from '@aibees/elephant/utils';
import { Icon, LinkButton, TextEllipsis } from '@aibees/shared/components';
import { HighlightableContent } from '@aibees/shared/features';
import { classnames, fHumanReadableNumber } from '@aibees/shared/utils';
import { Stack, Typography } from '@mui/material';
import { memo } from 'react';
import { ProductAndServicesInfo } from '../product-and-services-info';
import { InfoRow } from './components';
import { useCompanyLanguages } from './hooks/use-company-languages';
import * as Styled from './styles';
import * as SX from './sx';
import type { CompanyOverviewProps as Props } from './types';

export const CompanyOverview = memo(({ className, hideHeader }: Props) => {
  const { api } = useCompanyReadDataContext();
  const companySize = getCompanySizeRange(api?.data?.summary?.staff);
  const languages = useCompanyLanguages(api?.data?.languages);
  const domain = getCompanyDomain(api?.data?.link);
  const { showProducts } = getCompanyInfoSectionVisibility(api?.data);

  const actions = useTagActions();

  return (
    <Styled.Wrapper
      className={classnames('CompanyOverview', className)}
      sx={SX.root({ hideHeader })}
    >
      <EntityInfoPanel
        sx={SX.panel(hideHeader)}
        {...(!hideHeader && {
          icon: 'business-center',
          title: 'Company Overview',
        })}
      >
        <Stack sx={SX.container}>
          {showProducts && (
            <ProductAndServicesInfo
              config={{
                actions,
                field: SeaAllFilterField.products_and_services,
                placeholder: 'Search Product & Services',
              }}
            />
          )}
          {domain && (
            <InfoRow icon="world" label="Domain">
              <LinkButton variant="text" sx={SX.titleRow} href={domain}>
                <Icon color="grey.700" size={14} name="link-out" />
                <TextEllipsis variant="body4" fontWeight={700} color="gray.700">
                  {domain}
                </TextEllipsis>
              </LinkButton>
            </InfoRow>
          )}
          {!!api?.data?.statistics?.network?.followers_count && (
            <InfoRow icon="linkedin" label="Followers">
              <Typography variant="body4" fontWeight={700} color="grey.700">
                {fHumanReadableNumber(
                  api?.data?.statistics?.network?.followers_count
                )}
              </Typography>
            </InfoRow>
          )}
          {!!api?.data?.summary?.founded_year?.toString() && (
            <InfoRow icon="today-calendar" label="Founding Year">
              <HighlightableContent
                content={api?.data?.summary.founded_year.toString()}
                variant="body4"
                fontWeight={700}
                color="grey.700"
              />
            </InfoRow>
          )}

          {api?.data?.summary?.type && (
            <InfoRow icon="ic-type" label="Type">
              <HighlightableContent
                content={companyTypeFilterLabelParser(
                  api?.data?.summary.type,
                  api?.data?.summary.type
                )}
                variant="body4"
                fontWeight={700}
                color="grey.700"
              />
            </InfoRow>
          )}

          {(api?.data?.location?.headquarter?.country ||
            api?.data?.location?.headquarter?.city) && (
            <InfoRow icon="office-building-location" label="Company HQ">
              <TextEllipsis variant="body4" fontWeight={700} color="grey.700">
                {formatCompanyLocation([
                  api?.data?.location?.headquarter?.city,
                  api?.data?.location?.headquarter?.country,
                ])}
              </TextEllipsis>
            </InfoRow>
          )}

          {!!companySize && (
            <InfoRow icon="groups-outlined" label="Employee Size">
              <Typography variant="body4" fontWeight={700} color="grey.700">
                {companySize}
              </Typography>
            </InfoRow>
          )}

          {!!api?.data?.summary?.staff?.total && (
            <InfoRow icon="how-to-reg" label="Headcount">
              <Typography variant="body4" fontWeight={700} color="grey.700">
                {api?.data.summary?.staff?.total}
              </Typography>
            </InfoRow>
          )}

          {!!languages.summary && (
            <InfoRow icon="language" label="Operation Language">
              <TextEllipsis
                variant="body4"
                fontWeight={700}
                color="grey.700"
                tooltipValue={languages.full}
              >
                {languages.summary}
              </TextEllipsis>
            </InfoRow>
          )}

          {!!api?.data?.retail?.total && (
            <InfoRow icon="location-multiple" label="Retails Location">
              <TextEllipsis variant="body4" fontWeight={700} color="grey.700">
                {api?.data?.retail?.total}
              </TextEllipsis>
            </InfoRow>
          )}
        </Stack>
      </EntityInfoPanel>
    </Styled.Wrapper>
  );
});

CompanyOverview.displayName = 'CompanyOverview';
